import { useEffect, useState } from "react";

import { Role } from "@hrms/types";
import { api, errMsg } from "../../lib/api.js";
import { logError } from "../../lib/logger.js";
import { AsyncButton } from "../../components/ui/async-button.js";
import { Badge } from "../../components/ui/badge.js";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../../components/ui/card.js";
import { Input } from "../../components/ui/input.js";
import { Textarea } from "../../components/ui/textarea.js";

interface PolicyText {
  id: string; key: string; title: string; body: string; version: number;
  approvedAt: string | null; updatedAt: string;
}

// Where each text appears, for the admin editing it.
const USED_IN: Record<string, string> = {
  onboarding_data_protection_notice: "Onboarding wizard — shown above Aadhaar, PAN and bank uploads; the employee must accept it before uploading.",
  onboarding_document_retention: "Onboarding wizard — shown with the data-protection notice.",
};

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

// Admin → Policy Texts: the notice wording employees see. Ships as draft text;
// a Super HR Admin marks a version approved once legal signs off. Any edit
// creates a new, unapproved version.
export default function PolicyTextsPanel({ role }: { role: string }) {
  const canEdit = role === Role.SUPER_HR_ADMIN || role === Role.CORPORATE_HR;
  const canApprove = role === Role.SUPER_HR_ADMIN;
  const [texts, setTexts] = useState<PolicyText[]>([]);
  const [drafts, setDrafts] = useState<Record<string, { title: string; body: string }>>({});
  const [error, setError] = useState("");

  function load() {
    api.get<PolicyText[]>("/admin/policy-texts")
      .then(r => {
        setTexts(r.data);
        setDrafts(Object.fromEntries(r.data.map(t => [t.key, { title: t.title, body: t.body }])));
      })
      .catch((err) => { logError("PolicyTextsPanel.load", err); setError("Could not load policy texts."); });
  }
  useEffect(load, []);

  function replace(updated: PolicyText) {
    setTexts(prev => prev.map(t => (t.key === updated.key ? updated : t)));
    setDrafts(prev => ({ ...prev, [updated.key]: { title: updated.title, body: updated.body } }));
  }

  async function save(key: string) {
    setError("");
    try {
      const { data } = await api.put<PolicyText>(`/admin/policy-texts/${key}`, drafts[key]);
      replace(data);
    } catch (err) {
      logError("PolicyTextsPanel.save", err, { key });
      setError(errMsg(err) ?? "Could not save.");
      throw err;
    }
  }

  async function approve(key: string) {
    setError("");
    try {
      const { data } = await api.post<PolicyText>(`/admin/policy-texts/${key}/approve`, {});
      replace(data);
    } catch (err) {
      logError("PolicyTextsPanel.approve", err, { key });
      setError(errMsg(err) ?? "Could not mark as approved.");
      throw err;
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-[13px] text-surface-500">
        Wording shown to employees during onboarding. It ships as draft text: have legal review it, then mark the version approved.
      </p>
      {error && <p className="text-xs text-[var(--color-error-fg)]">{error}</p>}
      {texts.map(t => {
        const draft = drafts[t.key] ?? { title: t.title, body: t.body };
        const dirty = draft.title !== t.title || draft.body !== t.body;
        return (
          <Card key={t.key}>
            <CardHeader>
              <div className="flex flex-wrap items-center gap-2">
                <CardTitle>{t.title}</CardTitle>
                <Badge tone="outline">v{t.version}</Badge>
                {t.approvedAt
                  ? <Badge tone="success">Approved {fmtDate(t.approvedAt)}</Badge>
                  : <Badge tone="warning">Draft — pending legal approval</Badge>}
              </div>
              <CardDescription>{USED_IN[t.key] ?? t.key}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Input
                aria-label="Title"
                value={draft.title}
                disabled={!canEdit}
                onChange={e => setDrafts(prev => ({ ...prev, [t.key]: { ...draft, title: e.target.value } }))}
              />
              <Textarea
                aria-label="Body"
                rows={12}
                value={draft.body}
                disabled={!canEdit}
                onChange={e => setDrafts(prev => ({ ...prev, [t.key]: { ...draft, body: e.target.value } }))}
              />
              <p className="text-xs text-surface-500">Plain text. Blank lines separate paragraphs. Saving changes creates a new, unapproved version.</p>
            </CardContent>
            {(canEdit || canApprove) && (
              <CardFooter>
                {canEdit && (
                  <AsyncButton onClick={() => save(t.key)} disabled={!dirty || !draft.title.trim() || !draft.body.trim()}>
                    Save new version
                  </AsyncButton>
                )}
                {canApprove && !t.approvedAt && (
                  <AsyncButton variant="success" onClick={() => approve(t.key)} disabled={dirty}>
                    Mark v{t.version} approved
                  </AsyncButton>
                )}
              </CardFooter>
            )}
          </Card>
        );
      })}
    </div>
  );
}
